import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, ArrowRight, Shield, Activity, Crosshair } from 'lucide-react';
import { useGlobalToast } from '@/contexts/ToastContext';
import { useFormValidation, validation } from '@/utils/validation';
import { authService } from '@/services';

interface ForgotPasswordForm {
  email: string;
}

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const { success, error } = useGlobalToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Form validation
  const {
    data,
    errors,
    touched,
    updateField,
    touchField,
    validateAll
  } = useFormValidation<ForgotPasswordForm>(
    { email: '' },
    {
      email: validation.email
    }
  );

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const isFormValid = validateAll();
    if (!isFormValid) {
      touchField('email');
      return;
    }

    setIsLoading(true);
    try {
      const result = await authService.resetPassword(data.email);

      if (result.success) {
        success('Password reset email sent', 'Check your email for reset instructions');
        setIsSubmitted(true);
      } else {
        error('Reset failed', result.error || 'Failed to send reset email');
      }
    } catch (err: any) {
      error('Password Reset Failed', err.message || 'Unable to send reset email. Please check your email address and try again');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 font-sans relative flex items-center justify-center py-20 px-6">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white focus:rounded-lg focus:shadow-lg min-h-touch">
        Skip to main content
      </a>
      <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-gray-100"></div>

      <main id="main-content" role="main" className="relative z-10 w-full max-w-lg">
        <div className="bg-white p-10 shadow-xl relative rounded-2xl border border-gray-200">
          <div className="text-center mb-10">
            <div className="w-16 h-16 bg-primary-600 flex items-center justify-center shadow-lg mx-auto mb-6 rounded-xl">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Reset Password
            </h1>
            <p className="text-sm text-gray-600">
              Enter your email to receive reset instructions
            </p>
          </div>

          {!isSubmitted ? (
            <form onSubmit={onSubmit} className="space-y-6">
              <div className="space-y-4">
                <p className="text-sm text-gray-600 text-center">
                  Enter your registered email address below to receive password reset instructions.
                </p>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      value={data.email}
                      required
                      onChange={(e) => updateField('email', e.target.value)}
                      onBlur={() => touchField('email')}
                      className={`w-full pl-12 pr-4 py-3 bg-white border ${touched.email && errors.email ? 'border-red-300 bg-red-50' : 'border-gray-300'} text-gray-900 text-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 outline-none transition-all placeholder:text-gray-400 rounded-lg`}
                      placeholder="your@email.com"
                    />
                  </div>
                  {touched.email && errors.email && <p className="text-sm text-red-600">{errors.email}</p>}
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 bg-primary-600 text-white font-semibold text-sm shadow-sm hover:bg-primary-700 transition-all flex items-center justify-center rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    Send Reset Link
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </button>

              <div className="text-center pt-6 border-t border-gray-200">
                <Link to="/login" className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-all flex items-center justify-center gap-2">
                  <ArrowRight className="w-4 h-4 rotate-180" />
                  Back to Login
                </Link>
              </div>
            </form>
          ) : (
            <div className="text-center space-y-6 py-6">
              <div className="p-6 bg-emerald-50 border border-emerald-200 rounded-xl">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 bg-emerald-100 border-2 border-emerald-500 flex items-center justify-center rounded-xl">
                    <Mail className="w-8 h-8 text-emerald-600" />
                  </div>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Check Your Email</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  We've sent password reset instructions to your email address.
                </p>
              </div>

              <button
                onClick={() => setIsSubmitted(false)}
                className="text-sm font-medium text-gray-600 hover:text-primary-600 transition-all"
              >
                Didn't receive the email?
              </button>

              <div className="pt-6 border-t border-gray-200">
                <Link to="/login" className="px-6 py-3 bg-gray-100 border border-gray-300 text-gray-700 font-medium text-sm hover:bg-gray-200 transition-all rounded-lg inline-block">
                  Return to Login
                </Link>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}